export const stripHtml = (html: string): string => {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const textContent = tempDiv.textContent || tempDiv.innerText || "";
  return textContent
    .replace(/\u00A0/g, " ") // Non-breaking space
    .replace(/\u200B/g, "") // Zero-width space
    .replace(/\s+/g, " ") // Multiple whitespace to single space
    .trim();
};

export const isTextVisuallyEmpty = (text: string): boolean => {
  if (!text) return true;
  return stripHtml(text).length === 0;
};

/**
 * Formats plain text content into proper HTML paragraph structure for WYSIWYG editors.
 * Handles cross-platform line endings and intelligently creates paragraphs and line breaks.
 */
export const formatContentForDisplay = (content: string): string => {
  const normalizedContent = content
    .replace(/\r\n/g, "\n") // Convert Windows line endings to Unix
    .replace(/\r/g, "\n") // Convert old Mac line endings to Unix
    .trim(); // Remove leading/trailing whitespace

  // Check if content has paragraph breaks (double newlines)
  if (normalizedContent.includes("\n\n")) {
    // Content has paragraph structure - split on double newlines
    return normalizedContent
      .split(/\n\s*\n/) // Split on double newlines (paragraph breaks)
      .filter(paragraph => paragraph.trim().length > 0) // Remove empty paragraphs
      .map(paragraph => {
        // Replace single newlines within paragraphs with <br> tags
        const formattedParagraph = paragraph.trim().replace(/\n/g, "<br>");
        return `<p>${formattedParagraph}</p>`;
      })
      .join(""); // Join paragraphs without additional spacing
  } else {
    // Content doesn't have clear paragraph breaks - treat each line as potential paragraph
    const lines = normalizedContent
      .split("\n")
      .filter(line => line.trim().length > 0);
    if (lines.length <= 1) {
      // Single line or very short content - wrap in one paragraph
      return `<p>${normalizedContent}</p>`;
    } else {
      // Multiple lines - create paragraphs for better readability
      return lines.map(line => `<p>${line.trim()}</p>`).join("");
    }
  }
};

/**
 * Converts URLs in plain text to clickable HTML anchor tags and replaces
 * newline characters with <br> tags to preserve line breaks.
 * Handles https://, http://, www., and bare domain (e.g. google.com) patterns.
 */
/**
 * Replaces empty `<p>` tags with `<p>&nbsp;</p>` to preserve vertical spacing
 * in email clients, which collapse truly empty elements.
 *
 * Handles common ProseMirror/Kendo variants:
 *   <p></p>  |  <p><br></p>  |  <p><br/></p>  |  <p style="..."></p>
 *
 * Shared between the email preview modal and the send path so that
 * preview === send.
 */
export const normalizeEmptyParagraphs = (html: string): string => {
  if (!html) return html;
  return html.replace(
    /(<p\b[^>]*>)\s*(?:<br\s*\/?>)?\s*(<\/p>)/gi,
    "$1&nbsp;$2"
  );
};

export const convertUrlsToLinks = (text: string): string =>
  text
    .replace(
      /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+|[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}(?:\/[^\s<>"]*)?)/g,
      url => {
        const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        return `<a href="${href}" target="_blank" rel="noreferrer">${url}</a>`;
      }
    )
    .replace(/\n/g, "<br>");
